import React from 'react'

import styled from '@emotion/styled'
import { default as ReactToggle } from 'react-toggle'

import 'react-toggle/style.css'

import LoadingIcon from '../LoadingIcon/LoadingIcon'

export type ToggleVariant = 'primary' | 'warning'
export type ToggleBaseProps = {
  loading?: boolean
  variant?: ToggleVariant
}

const TRANSITION_DURATION_AND_EASING_FUNCTION = '0.2s ease'

const StyledToggle = styled(ReactToggle, { shouldForwardProp: prop => prop !== 'loading' })<ToggleBaseProps>`
  /* stylelint-disable no-descending-specificity -- legacy CSS targetting a library */
  .react-toggle-track,
  &.react-toggle:hover .react-toggle-track {
    background: ${({ theme }) => theme.colors.toggleOffButton};
    width: 3.5rem;
    height: 2rem;
  }

  &.react-toggle {
    transition: opacity 600ms;
  }

  &.react-toggle.react-toggle--disabled {
    ${props => props.loading && 'opacity: 0.75;'}
  }

  &.react-toggle--checked .react-toggle-track {
    ${props => props.loading && 'opacity: 0.25;'}
  }

  &.react-toggle:active .react-toggle-thumb {
    box-shadow: none;
  }

  /* Toggle thumb - more specific selectors */
  &.react-toggle .react-toggle-thumb {
    box-shadow: none;
    border: none;
    top: 0.25rem;
    left: 0.25rem;
    height: 1.5rem;
    width: 1.5rem;
    transition: left ${TRANSITION_DURATION_AND_EASING_FUNCTION};
  }

  /* toggle off state */
  &.react-toggle:hover:not(.react-toggle--disabled) .react-toggle-track {
    background: ${({ theme }) => theme.colors.toggleOffButtonHover};
  }

  &.react-toggle--focus .react-toggle-thumb,
  &.react-toggle:active:not(.react-toggle--disabled) .react-toggle-thumb {
    box-shadow: none;
  }

  &.react-toggle:active:not(.react-toggle--disabled) .react-toggle-track {
    background: ${({ theme }) => theme.colors.toggleOffButtonActive};
  }

  /* toggle on state */
  &.react-toggle--checked .react-toggle-track,
  &.react-toggle--checked:hover .react-toggle-track {
    background: ${({ theme, variant = 'primary' }) => theme.colors[variant]};
  }

  &.react-toggle--checked .react-toggle-thumb {
    left: 1.75rem;
  }

  &.react-toggle--checked:hover:not(.react-toggle--disabled) .react-toggle-track {
    background: ${({ theme, variant = 'primary' }) =>
      variant === 'primary' ? theme.colors.primaryButtonHover : theme.colors.warning};
  }

  &.react-toggle--checked:active:not(.react-toggle--disabled) .react-toggle-track {
    background: ${({ theme, variant = 'primary' }) =>
      variant === 'primary' ? theme.colors.primaryButtonActive : theme.colors.warning};
  }

  ${props => props.theme.selectors.keyboard}
  &.react-toggle--focus
    .react-toggle-track {
    outline: 0.5rem solid ${({ theme, variant = 'primary' }) => theme.addOpacity(theme.colors[variant], 0.2)};
  }
`
const ToggleContainer = styled.div`
  position: relative;
  font-size: 0; /* stylelint-disable-line @cash-web/no-hardcoded-font-styles */
`

const ToggleLoadingIcon = styled('div', { shouldForwardProp: prop => prop !== 'loading' })<{
  checked?: boolean
  loading?: boolean
}>`
  position: absolute;
  z-index: 10;
  transition: all ${TRANSITION_DURATION_AND_EASING_FUNCTION};
  left: ${({ checked }) => (checked ? '1.5rem' : '0')};
  opacity: ${({ loading }) => (loading ? 1 : 0)};
`

export type ToggleProps = Omit<React.ComponentProps<typeof StyledToggle>, 'icons'> & ToggleBaseProps

export const Toggle = (props: ToggleProps) => {
  return (
    <ToggleContainer>
      <ToggleLoadingIcon checked={props.checked} loading={props.loading} data-testid="loading-icon-wrapper">
        {props.loading ? <LoadingIcon size={32} color={props.variant} /> : null}
      </ToggleLoadingIcon>
      <StyledToggle data-testid="styled-toggle" {...props} icons={false} />
    </ToggleContainer>
  )
}
